// The mover's controls over the board (qa-runs#1161): the picker (Move preselected, Double move, Run) while a token
// with a speed is selected or carried, what is left of a Move, the note for a token with no speed, and the
// confirmation a drop waits on. Buttons only — every control works by a tap (no hover), sized for a finger.
import { useEffect, useState } from 'react';
import { NO_SPEED_NOTE, stepTakenNote } from '@dragons-table/vtt-shared';
import { useTableState } from '../table/TableState';
import { floorOffer, floorView, type FloorOffer } from './floorView';
import { moveStore, type MoveView } from './moveArea';

const tap = 'min-h-[44px] min-w-[44px] rounded px-3 py-1 text-xs font-medium';

export function useMoveView(): MoveView {
  const [v, setV] = useState(moveStore.get());
  useEffect(() => moveStore.subscribe(setV), []);
  return v;
}

export function MovePrompt() {
  const v = useMoveView();
  if (v.pending) {
    return <PendingBar key={`${v.pending.id}|${v.pending.label}`} />;
  }
  return <><Picker /><FloorOfferBar /></>;
}

/** A player's token has reached an opening (qa-runs#1240): the floors the server says join there, a tap from going. */
function FloorOfferBar() {
  const { store, act, setNotice } = useTableState();
  const [o, setO] = useState<FloorOffer | null>(floorOffer.get());
  useEffect(() => floorOffer.subscribe(setO), []);
  const token = o ? store.state.objects[o.id] as { kind: string; name?: string; x: number; y: number; rev?: number } | undefined : undefined;
  if (!o || token?.kind !== 'token') return null;
  const go = (id: string) => {
    act({ kind: 'object.move', id: o.id, route: [{ x: token.x, y: token.y }], rev: token.rev ?? 0, floor: id }, (reply) => {
      if (!reply.ok) { if (reply.reason) setNotice(reply.reason); return; }
      floorView.set(id);
    });
  };
  return (
    <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded bg-stone-900/95 px-3 py-1 text-xs text-stone-200" data-testid="floor-offer" role="group" aria-label="Change floor">
      <span>{`${token.name ?? 'Your token'} is at an opening.`}</span>
      {o.floors.map((f) => <button key={f.id} type="button" className={`${tap} border border-sky-600`} data-testid={`floor-offer-${f.id}`} onClick={() => go(f.id)}>{`To ${f.name}`}</button>)}
      <button type="button" className={`${tap} border border-stone-500 hover:bg-stone-800`} onClick={() => floorOffer.set(null)}>Stay here</button>
    </div>
  );
}

/** The confirm bar (qa-runs#1161), with the height control and the stairs (qa-runs#1160) when the scene has them. */
function PendingBar() {
  const v = useMoveView();
  const p = v.pending!;
  const [dz, setDz] = useState(0);
  const [floor, setFloor] = useState<string | null>(null);
  const h = p.height;
  const up = h && dz ? `${dz > 0 ? '+' : '−'}${Math.round(Math.abs(dz) * h.perSquare * 100) / 100} ${h.unit} ${dz > 0 ? 'up' : 'down'}` : '';
  const extra = (): { floor?: string; z?: number } => ({ ...(floor ? { floor } : {}), ...(h && dz ? { z: h.z + dz } : {}) });
  return (
      <div className="pointer-events-auto flex flex-wrap items-center gap-2 rounded bg-stone-900/95 px-3 py-1 text-xs text-stone-200" data-testid="move-confirm" role="group" aria-label="Confirm the move">
        <span data-testid="move-confirm-label">{p.label}{up ? ` · ${up}` : ''}</span>
        {h && (
          <span className="flex items-center gap-1" role="group" aria-label="Height">
            <button type="button" className={`${tap} border border-stone-600`} aria-label="Lower 5 ft" data-testid="move-height-down" onClick={() => setDz(dz - 1)}>−</button>
            <button type="button" className={`${tap} border border-stone-600`} aria-label="Raise 5 ft" data-testid="move-height-up" onClick={() => setDz(dz + 1)}>+</button>
          </span>
        )}
        {p.floors?.map((f) => (
          <button key={f.id} type="button" aria-pressed={floor === f.id} data-testid={`move-floor-${f.id}`}
            className={`${tap} ${floor === f.id ? 'bg-sky-700 text-white' : 'border border-sky-600'}`} onClick={() => setFloor(floor === f.id ? null : f.id)}>{`To ${f.name}`}</button>
        ))}
        <button type="button" className={`${tap} bg-emerald-700 text-white hover:bg-emerald-600`} onClick={() => { moveStore.setPending(null); p.confirm(extra()); }}>Confirm</button>
        <button type="button" className={`${tap} border border-stone-500 hover:bg-stone-800`} onClick={() => { moveStore.setPending(null); p.cancel(); }}>Cancel</button>
      </div>
  );
}

function Picker() {
  const v = useMoveView();
  const { systemRules } = useTableState();
  const unit = systemRules.distance;
  const plan = v.plan;
  if (!v.token || !plan || plan.mode === 'free') return null;
  if (plan.mode === 'stepped') {
    return <p className="pointer-events-none rounded bg-stone-900/95 px-3 py-1 text-xs text-amber-200" data-testid="move-stepped" role="status">{stepTakenNote(plan.kind.label)}</p>;
  }
  if (plan.mode === 'nospeed') {
    return <p className="pointer-events-none rounded bg-stone-900/95 px-3 py-1 text-xs text-amber-200" data-testid="move-no-speed" role="status">{NO_SPEED_NOTE}</p>;
  }
  if (plan.mode === 'rest') {
    const left = unit ? Math.round(plan.budget * unit.perSquare * 100) / 100 : plan.budget;
    return <p className="pointer-events-none rounded bg-stone-900/95 px-3 py-1 text-xs text-stone-200" data-testid="move-rest" role="status">{`Left of the move: ${left} ${unit?.unit ?? ''}`.trim()}. Drag the token on to use it.</p>;
  }
  return (
    <div className="pointer-events-auto flex flex-wrap items-center gap-1 rounded bg-stone-900/95 px-2 py-1 text-xs text-stone-200" data-testid="move-picker" role="radiogroup" aria-label="Kind of move">
      {plan.kinds.map((k) => (
        <button key={k.key} type="button" role="radio" aria-checked={plan.kind.key === k.key}
          className={`${tap} ${plan.kind.key === k.key ? 'bg-amber-600 text-stone-950' : 'border border-stone-600 hover:bg-stone-800'}`}
          onClick={() => moveStore.pickKind(k.key)}>{k.label}</button>
      ))}
    </div>
  );
}
